import { Component, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { ReleaseService } from '../../core/services/release.service';
import { ToastService } from '../../shared/services/toast.service';
import { CardModule } from '@openng/optimus-ui/card';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { TagModule } from '@openng/optimus-ui/tag';
import { ButtonModule } from '@openng/optimus-ui/button';

interface PluginInfo {
  name: string;
  /** Newest release known to the CMS; null when nothing is published yet. */
  version: string | null;
  lastUpdated: Date | null;
}

/**
 * Injected by the Revit plugin into the WebView2 before this page runs.
 * Absent when the page is opened in a normal browser.
 */
interface AngulerisPluginBridge {
  version?: string;
  environment?: string;
  sendMessage(message: string): void;
}

interface UserInfo {
  id: string;
  fullName: string;
  email: string;
  position: string;
  role: string;
}

@Component({
  selector: 'app-plugin-dashboard',
  imports: [CommonModule, CardModule, ProgressSpinnerModule, TagModule, ButtonModule],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './plugin-dashboard.component.html'
})
export class PluginDashboardComponent implements OnInit {
  userInfo = signal<UserInfo | null>(null);
  pluginInfo = signal<PluginInfo | null>(null);
  isLoading = signal(true);
  updateChecking = signal(true);
  /** Version of the add-in this page is hosted in; null when opened outside the plugin. */
  installedVersion = signal<string | null>(null);
  /** Deployment the add-in talks to (Local / Staging / Production); null outside the plugin. */
  environment = signal<string | null>(null);

  private readonly authService = inject(AuthService);
  private readonly releaseService = inject(ReleaseService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  async ngOnInit() {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/plugin/login']);
      return;
    }

    const bridge = this.getPluginBridge();
    this.installedVersion.set(bridge?.version ?? null);
    this.environment.set(bridge?.environment ?? null);
    this.loadUserData();
    this.checkForUpdates();
  }

  private getPluginBridge(): AngulerisPluginBridge | null {
    return (window as unknown as { angulerisPlugin?: AngulerisPluginBridge }).angulerisPlugin ?? null;
  }

  /**
   * Ends the session. The server call goes first so the refresh cookie is revoked while this page is
   * still alive; only then is the plugin told, because it reacts by closing this very window and
   * bringing the login window back.
   */
  logout(): void {
    this.authService.logout().subscribe(() => {
      try {
        this.getPluginBridge()?.sendMessage('LOGOUT');
      } catch (error) {
        console.warn('Plugin communication not available:', error);
      }

      this.router.navigate(['/plugin/login']);
    });
  }

  private loadUserData() {
    try {
      this.isLoading.set(true);
      const user = this.authService.getCurrentUser();
      if (user) {
        this.userInfo.set({
          id: user.id,
          fullName: user.fullName || 'Unknown',
          email: user.email,
          position: user.position || 'Not specified',
          role: user.role?.name || this.getSystemRoleName(user.systemRole)
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      this.toastService.error('Failed to load user information');
    } finally {
      this.isLoading.set(false);
    }
  }

  private checkForUpdates() {
    this.updateChecking.set(true);
    this.releaseService.getReleases().subscribe({
      next: (releases) => {
        const publishedReleases = releases.filter(r => r.isPublished);
        if (publishedReleases.length > 0) {
          const latest = publishedReleases.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0];
          this.pluginInfo.set({
            name: 'Anguleris Revit Plugin',
            version: latest.version,
            lastUpdated: new Date(latest.createdAt)
          });
        } else if (releases.length > 0) {
          const latest = releases.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0];
          this.pluginInfo.set({
            name: 'Anguleris Revit Plugin',
            version: `${latest.version} (unpublished)`,
            lastUpdated: new Date(latest.createdAt)
          });
        } else {
          // No release to compare against; the template renders this as "None published"
          // rather than inventing a version string and a release date.
          this.pluginInfo.set({
            name: 'Anguleris Revit Plugin',
            version: null,
            lastUpdated: null
          });
        }
        this.updateChecking.set(false);
      },
      error: () => {
        this.pluginInfo.set({
          name: 'Anguleris Revit Plugin',
          version: null,
          lastUpdated: null
        });
        this.updateChecking.set(false);
      }
    });
  }

  /** Version strings come from git tags, which may or may not already carry the "v". */
  formatVersion(version: string | null): string {
    if (!version) {
      return '—';
    }
    return version.startsWith('v') ? version : `v${version}`;
  }

  /** True when the running add-in is not older than the newest release the CMS knows about. */
  isUpToDate(): boolean {
    const installed = this.installedVersion();
    const latest = this.pluginInfo()?.version;
    if (!installed || !latest) {
      return true;
    }
    return this.compareVersions(installed, latest) >= 0;
  }

  private compareVersions(left: string, right: string): number {
    const parse = (value: string) => value.replace(/^v/, '').split(/[^0-9]+/).map(part => Number(part) || 0);
    const a = parse(left);
    const b = parse(right);
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      const diff = (a[i] ?? 0) - (b[i] ?? 0);
      if (diff !== 0) {
        return diff < 0 ? -1 : 1;
      }
    }
    return 0;
  }


  getRoleDisplayName(role: string): string {
    switch (role?.toLowerCase()) {
      case 'super administrator':
        return 'Super Administrator';
      case 'administrator':
        return 'Administrator';
      case 'bim manager':
        return 'BIM Manager';
      case 'designer':
        return 'Designer';
      default:
        return role || 'Unknown';
    }
  }

  getRoleSeverity(role: string): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" | null | undefined {
    switch (role?.toLowerCase()) {
      case 'super administrator':
        return 'danger';
      case 'administrator':
        return 'success';
      case 'bim manager':
        return 'info';
      case 'designer':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  canOpenTelemetry(): boolean {
    const role = this.authService.getCurrentUser()?.systemRole ?? 0;
    return role === 1 || role === 2 || role === 3;
  }

  openReleases(): void {
    this.router.navigate(['/plugins/releases']);
  }

  openTelemetry(): void {
    this.router.navigate(['/plugins/telemetry']);
  }

  private getSystemRoleName(systemRole?: number): string {
    switch (systemRole) {
      case 1:
        return 'Super Administrator';
      case 2:
        return 'Administrator';
      case 3:
        return 'BIM Manager';
      case 4:
        return 'Designer';
      default:
        return 'Unknown';
    }
  }
}
